import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { TrustBar } from '@/components/TrustBadges';
import { ShieldCheck, EnvelopeSimple } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { token } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [creatingInvites, setCreatingInvites] = useState(false);

  // Post-booking gate → create an event from this booking group and open the
  // prefilled Invitations builder. On finish it continues to /coordinator.
  const startInvitations = async () => {
    setCreatingInvites(true);
    try {
      const res = await axios.post(`${API}/invite-events`, { source: 'booking', session_id: sessionId },
        { headers: { Authorization: `Bearer ${token}` } });
      navigate(`/events/${res.data.event_id}/invitations?from=booking`);
    } catch (err) {
      navigate('/coordinator');
    }
  };

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cancelled) return;
        if (res.data.payment_status === 'paid') { setStatus('paid'); return; }
        if (res.data.status === 'expired') { setStatus('expired'); return; }
      } catch (err) {
        // A transient error is NOT proof of payment — retry, never assume success.
        console.error('Payment status check error:', err);
      }
      attempts += 1;
      if (attempts >= 6) { setStatus('processing'); return; }
      setTimeout(poll, 1500);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, token]);

  return (
    <div className="min-h-screen bg-hero-wash" data-testid="checkout-success-page">
      <Navbar />
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-20 text-center space-y-5">
        {status === 'checking' && (
          <>
            <div className="w-16 h-16 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            <h1 className="text-3xl font-display font-semibold tracking-tight text-foreground">Verifying payment...</h1>
            <p className="text-sm text-muted-foreground leading-relaxed">Please wait while we confirm your payment.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <div className="relative w-20 h-20 mx-auto fade-up">
              {/* Playful coral confetti dots */}
              <span aria-hidden="true" className="absolute -top-1 -left-2 w-2 h-2 rounded-full bg-accent-coral" />
              <span aria-hidden="true" className="absolute -top-3 right-3 w-1.5 h-1.5 rounded-full bg-accent-coral/60" />
              <span aria-hidden="true" className="absolute bottom-0 -right-2 w-2.5 h-2.5 rounded-full bg-accent-coral/40" />
              <div className="w-20 h-20 bg-primary text-primary-foreground rounded-full flex items-center justify-center shadow-lift">
                <ShieldCheck size={36} weight="fill" />
              </div>
            </div>
            <div className="fade-up" style={{ animationDelay: '50ms' }}>
              <span className="inline-block rounded-full bg-accent-coral/10 text-accent-coral text-xs font-semibold px-3 py-1 mb-3">Payment secured</span>
              <h1 className="text-4xl md:text-5xl font-display font-semibold tracking-tight leading-[1.1] text-foreground">Booking confirmed!</h1>
            </div>
            <p className="text-base text-muted-foreground leading-relaxed fade-up" style={{ animationDelay: '100ms' }}>Your payment has been processed. Your provider will receive a notification and confirm the booking shortly. You can message them directly from your dashboard.</p>
            {/* Post-booking gate: invitations before the coordinator screen */}
            <div className="bg-card rounded-2xl border-2 border-primary/25 shadow-lift p-5 text-left fade-up" style={{ animationDelay: '150ms' }} data-testid="invites-gate">
              <div className="flex items-start gap-3">
                <span className="w-11 h-11 rounded-xl bg-accent-coral/10 text-accent-coral flex items-center justify-center shrink-0"><EnvelopeSimple size={22} weight="fill" /></span>
                <div className="flex-1">
                  <p className="font-display font-semibold text-foreground text-lg leading-tight">Would you like to send invitations?</p>
                  <p className="text-sm text-muted-foreground mt-0.5">Your event details are ready — pick a design and invite your guests in minutes.</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button onClick={startInvitations} disabled={creatingInvites} className="bg-primary text-primary-foreground rounded-xl px-5 h-10 text-sm font-semibold btn-anim shadow-soft hover:bg-primary/90 disabled:opacity-60 cursor-pointer" data-testid="invites-yes">{creatingInvites ? 'Opening…' : 'Yes, create invitations →'}</button>
                    <button onClick={() => navigate('/coordinator')} className="border border-border bg-card text-foreground rounded-xl px-5 h-10 text-sm font-semibold btn-anim hover:bg-secondary/60 cursor-pointer" data-testid="invites-no">No thanks</button>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-4 flex items-center gap-3 fade-up" style={{ animationDelay: '200ms' }}>
              <ShieldCheck size={18} className="text-primary shrink-0" />
              <span className="text-xs text-muted-foreground text-left leading-relaxed">Protected by Bookr. Your payment is held securely until the service is complete.</span>
            </div>
            <div className="flex flex-wrap gap-3 justify-center pt-2 fade-up" style={{ animationDelay: '250ms' }}>
              <button onClick={() => navigate('/coordinator')} className="border border-border bg-card text-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim hover:bg-secondary/60 cursor-pointer" data-testid="go-coordinator">Plan the day with your coordinator →</button>
              <button onClick={() => navigate('/dashboard')} className="border border-border bg-card text-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim hover:bg-secondary/60 cursor-pointer" data-testid="go-dashboard">Go to Dashboard</button>
              <button onClick={() => navigate('/messages')} className="border border-border bg-card text-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim hover:bg-secondary/60 cursor-pointer" data-testid="go-messages">Message Provider</button>
            </div>
            <TrustBar className="mt-4" />
          </>
        )}
        {status === 'processing' && (
          <>
            <div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto">
              <ShieldCheck size={28} />
            </div>
            <h1 className="text-3xl font-display font-semibold tracking-tight text-foreground">Payment processing</h1>
            <p className="text-sm text-muted-foreground leading-relaxed">Your payment is still being confirmed — this can take a moment. Your booking will appear in your dashboard as soon as it's done, and you won't be charged twice.</p>
            <button onClick={() => navigate('/dashboard')} className="bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 cursor-pointer" data-testid="go-dashboard-processing">Go to Dashboard</button>
          </>
        )}
        {status === 'expired' && (
          <>
            <div className="w-16 h-16 bg-accent-coral/10 text-accent-coral rounded-full flex items-center justify-center mx-auto text-2xl font-semibold">!</div>
            <h1 className="text-3xl font-display font-semibold tracking-tight text-foreground">Payment expired</h1>
            <p className="text-sm text-muted-foreground leading-relaxed">Your checkout session has expired. Please try again.</p>
            <button onClick={() => navigate('/browse')} className="bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 cursor-pointer">Back to Browse</button>
          </>
        )}
        {status === 'error' && (
          <>
            <h1 className="text-3xl font-display font-semibold tracking-tight text-foreground">Something went wrong</h1>
            <p className="text-sm text-muted-foreground leading-relaxed">We couldn't verify your payment. Please check your dashboard or contact support.</p>
            <button onClick={() => navigate('/dashboard')} className="bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 cursor-pointer">Go to Dashboard</button>
          </>
        )}
      </div>
    </div>
  );
}
